import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Send } from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

export default function Messages() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const listRef = useRef();

  const loadThreads = useCallback(async () => {
    const { data } = await api.get("/api/messages/threads");
    setThreads(data);
    if (data.length > 0 && !active) setActive(data[0]);
  }, [active]);
  useEffect(() => { loadThreads(); }, [loadThreads]);

  useEffect(() => {
    if (!active) return;
    (async () => {
      const { data } = await api.get(`/api/messages/thread/${active.thread_id}`);
      setMsgs(data);
      setTimeout(() => listRef.current?.scrollTo({ top: 999999 }), 100);
    })();
  }, [active]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      const { data } = await api.post("/api/messages", {
        thread_id: active.thread_id,
        to_user_id: active.counterparty.id,
        body: text.trim(),
      });
      setMsgs([...msgs, data]);
      setText("");
      setTimeout(() => listRef.current?.scrollTo({ top: 999999 }), 100);
    } catch (e) {
      console.error("send_message_failed", e);
      toast.error(t("messages.toast_send_failed"));
    }
  };

  return (
    <DashboardLayout title={t("messages.title")} subtitle={t("messages.subtitle")}>
      {threads.length === 0 ? (
        <div className="kpi-card p-8 text-center text-slate-500">
          {t("messages.empty")}
        </div>
      ) : (
        <div className="grid md:grid-cols-[320px_1fr] gap-4 h-[70vh]">
          <div className="kpi-card p-3 overflow-y-auto">
            {threads.map((t) => (
              <button
                key={t.thread_id}
                onClick={() => setActive(t)}
                data-testid={`thread-${t.thread_id}`}
                className={`w-full text-left px-3 py-3 rounded-lg mb-1 ${active?.thread_id === t.thread_id ? "bg-slate-100" : "hover:bg-slate-50"}`}
              >
                <div className="flex items-center gap-3">
                  <Avatar className="h-9 w-9"><AvatarFallback className="bg-petroleum text-gold text-xs">{(t.counterparty.name || "?").slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-petroleum truncate">{t.counterparty.name}</p>
                      {t.unread > 0 && <Badge className="bg-coral text-white hover:bg-coral">{t.unread}</Badge>}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{t.last_body}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <div className="kpi-card flex flex-col p-0">
            {active ? (
              <>
                <div className="border-b border-slate-200 p-4">
                  <p className="text-xs text-slate-500 uppercase tracking-widest">{t("messages.conversation_with")}</p>
                  <p className="font-display text-xl text-petroleum">{active.counterparty.name}</p>
                </div>
                <div ref={listRef} className="flex-1 overflow-y-auto p-6 space-y-3 bg-slate-50">
                  {msgs.map((m) => {
                    const mine = m.from_user_id === user.id;
                    return (
                      <div key={m.id} className={mine ? "flex justify-end" : "flex justify-start"}>
                        <div className={mine ? "bg-petroleum text-white rounded-2xl rounded-br-sm px-4 py-2 max-w-[70%]" : "bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-2 max-w-[70%]"}>
                          <p className="text-sm">{m.body}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <form onSubmit={send} className="border-t border-slate-200 p-3 flex gap-2 bg-white">
                  <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("messages.placeholder")} data-testid="input-message" />
                  <Button type="submit" data-testid="button-send-message" className="bg-petroleum text-white hover:bg-petroleum/90"><Send size={14} /></Button>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-500">{t("messages.select_conversation")}</div>
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
